import { Card } from "@/components/ui/card";
import { TaskStatus, Task } from "@/graphql/__generated__/graphql";
import { JSX, useEffect, useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
} from "@dnd-kit/core";
import ColumnComponent from "./ColumnComponent";
import { useUpdateTasksMutationHook } from "@/hooks";
import { cn } from "@/lib/utils";
import { handleDragStart, handleDragEnd } from "./helpers";
import { useTranslation } from "react-i18next";


interface TasksBoardProps {
  tasks: Task[];
  type: "user" | "tender";
  loading?: boolean;
  canEdit?: boolean;
  className?: string;
};

/**
 * TasksBoardComponent - Main component to render the tasks board.
 * Contains the layout and the main drag-and-drop logic.
 * @param tasks - Array of tasks to render
 * @param type - Type of taskboard (user's or tender's tasksboard).
 * The type is used to determine how to render the tasksbaord.
 * If the type is "user", the tasksboard will render the tasksboard without any editing functionality and is only for viewing.
 * If the type is "tender", the tasksboard will render the tasksboard with editing functionality.
 * @param loading - Flag to indicate if the tasks are still loading.
 * @param canEdit - Flag to indicate if the tasks can be edited. This is for permissions control.
 * @returns {JSX.Element} - The rendered TasksBoardComponent.
 */
export function TasksBoardComponent({
  tasks,
  type,
  loading,
  canEdit = true,
  className = ""
}: TasksBoardProps): JSX.Element {
  const { t } = useTranslation(["components", "common"]);

  // Deep clone tasks so we can safely mutate the order locally
  const clonedTasks = structuredClone(tasks);

  // Group the tasks according to TaskStatus
  const { todoTasks, inProgressTasks, doneTasks } = clonedTasks.reduce(
    (acc: { todoTasks: Task[]; inProgressTasks: Task[]; doneTasks: Task[] }, task) => {
      switch (task.status) {
      case TaskStatus.Todo:
        acc.todoTasks.push(task);
        break;
      case TaskStatus.InProgress:
        acc.inProgressTasks.push(task);
        break;
      case TaskStatus.Done:
        acc.doneTasks.push(task);
        break;
      default:
        break;
      }
      return acc;
    },
    { todoTasks: [], inProgressTasks: [], doneTasks: [] }
  );

  // Local state to store columns
  const [columns, setColumns] = useState<Record<TaskStatus, Task[]>>({
    [TaskStatus.Todo]: todoTasks,
    [TaskStatus.InProgress]: inProgressTasks,
    [TaskStatus.Done]: doneTasks,
  });

  // The current active tasks that is being drag, this is used to show the drag overlay
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  // Mutation Hooks
  const { updateTasksMutation } = useUpdateTasksMutationHook();

  const columnsToRender = [
    {
      key: TaskStatus.Todo,
      title: t("common:taskstatus.todo"),
      tasks: columns.TODO,
      taskStatus: TaskStatus.Todo
    },
    {
      key: TaskStatus.InProgress,
      title: t("common:taskstatus.in_progress"),
      tasks: columns.IN_PROGRESS,
      taskStatus: TaskStatus.InProgress
    },
    {
      key: TaskStatus.Done,
      title: t("common:taskstatus.done"),
      tasks: columns.DONE,
      taskStatus: TaskStatus.Done
    }
  ];

  // Re-sync columns from server whenever `tasks` changes so the UI updates with the latest data
  useEffect(() => {
    setColumns({
      [TaskStatus.Todo]: todoTasks,
      [TaskStatus.InProgress]: inProgressTasks,
      [TaskStatus.Done]: doneTasks,
    });
  }, [tasks]);

  return (
    /**
     * Drag and drop context wrapper
     * collisionDetection - https://docs.dndkit.com/api-documentation/context-provider/collision-detection-algorithms
     */
    <DndContext
      collisionDetection={closestCorners}
      onDragStart={(event) => handleDragStart(event, columns, setActiveTask)}
      onDragEnd={(event) => handleDragEnd(event, columns, setActiveTask, setColumns, updateTasksMutation)}
    >
      <div className={cn("flex flex-start gap-4 pr-4 py-4", className)}>
        {
          columnsToRender.map(({ key, title, tasks, taskStatus }) => (
            <ColumnComponent
              key={key}
              title={title}
              taskStatus={taskStatus}
              tasks={tasks}
              type={type}
              loading={loading}
              canEdit={canEdit}
            />
          ))
        }
      </div>

      <DragOverlay>
        {activeTask && (
          <Card className="p-4 bg-gray-100 border border-gray-200 shadow-md">
            {activeTask.title}
          </Card>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default TasksBoardComponent;
